import React, { useEffect, useRef, useState } from "react";
import { COMMENT_MAX_LENGTH, validateCommentText } from "../utils/checklistComments";

// Mirrors ChecklistNotesHistory.js:22-40's author/timestamp resolution
// exactly. Duplicated rather than imported (that file exports neither
// helper, and NG16 marks it off-limits for edits) — the same small-helper
// duplication precedent D9 already uses for the panel chrome CSS.
const actorDisplayName = (comment) => {
  const user = comment?.created_by_user;
  const name = (
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ")
  ).trim();
  return name || "Unknown user";
};

const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

// Inline clock glyph for the timestamp, matching the reference layout.
const ClockIcon = () => (
  <svg
    className="checklist-comment-item-clock"
    viewBox="0 0 640 640"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M320 112C205.1 112 112 205.1 112 320C112 434.9 205.1 528 320 528C434.9 528 528 434.9 528 320C528 205.1 434.9 112 320 112zM64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576C178.6 576 64 461.4 64 320zM296 184C296 170.7 306.7 160 320 160C333.3 160 344 170.7 344 184L344 310.1L413 379.1C422.4 388.5 422.4 403.7 413 413C403.6 422.3 388.4 422.4 379.1 413L303 336.9C298.5 332.4 296 326.3 296 319.9L296 184z"
    />
  </svg>
);

/**
 * One comment row (TRD §2.2.3, §3.10) — view mode, or, for the current
 * user's own comment, an inline edit mode. Purely prop-driven: it issues no
 * API calls itself. `onSaveEdit`/`onRequestDelete` hand the trimmed text or
 * the delete intent to the container (ChecklistComments, CLCMT-F006), which
 * owns the request, `isSaving`, and `editError`.
 *
 * Props:
 * - comment:         { id, comment, created_at, edited_at, created_by_user }
 * - isOwn:           bool — computed by the container via isOwnComment()
 * - canModerate:     bool — P1/F8 only. Enables Delete on a non-own comment.
 *                    Must NEVER enable Edit (NG7, AC-F8.5) — this component
 *                    never reads canModerate when deciding whether to show
 *                    the Edit control.
 * - isEditing:       bool — true when the container has this row's editor
 *                    open (the container enforces exactly one editor open
 *                    at a time across the list, AC-F4.8)
 * - isSaving:        bool — true while the container's PUT is in flight
 * - editError:       string | null — rendered inline inside the editor,
 *                    never a toast
 * - onStartEdit:     () => void
 * - onCancelEdit:    () => void — issues no API call (AC-F4.3)
 * - onSaveEdit:      (trimmedText: string, expectedEditedAt: string | null)
 *                    => void — CLCMT-F011/F10: `expectedEditedAt` is the
 *                    `edited_at` this editor was opened against (frozen at
 *                    open time, not re-read on every render — see the
 *                    `openedAgainstEditedAtRef` note below), or the current
 *                    server value from `staleConflict` when retrying after
 *                    a conflict.
 * - onRequestDelete: () => void
 * - staleConflict:   { currentText: string, currentEditedAt: string | null }
 *                    | null — CLCMT-F011/F10. Set by the container after a
 *                    409 CommentStaleError and a follow-up refetch (the
 *                    error body itself carries no custom properties, TR5).
 *                    Rendered beside the still-open, still-preserved draft
 *                    with an explicit Keep mine / Discard choice.
 */
const ChecklistCommentItem = ({
  comment,
  isOwn,
  canModerate,
  isEditing,
  isSaving,
  editError,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRequestDelete,
  staleConflict,
}) => {
  const originalText = comment?.comment ?? "";
  const [draft, setDraft] = useState(originalText);
  const [touched, setTouched] = useState(false);
  const textareaRef = useRef(null);
  // Same belt-and-braces double-submit guard as ChecklistCommentComposer /
  // StatusChangeNotesModal.js, released once the caller reports isSaving
  // has dropped.
  const submittingRef = useRef(false);
  // CLCMT-F011/F10 (AC-F10.1): the `edited_at` this editor was opened
  // against, frozen once when editing starts rather than read fresh from
  // `comment` on every render — a background refetch triggered by some
  // *other* row's write must not silently swap in a newer value here and
  // defeat the whole point of the precondition. Sent as `expected_edited_at`
  // on save; superseded by `staleConflict.currentEditedAt` on a retry.
  const openedAgainstEditedAtRef = useRef(null);

  // Fresh draft and focus whenever this row's editor opens (AC-F4.2), and
  // reset the double-submit guard so re-opening after a prior save doesn't
  // inherit a stale guard.
  useEffect(() => {
    if (isEditing) {
      setDraft(originalText);
      setTouched(false);
      submittingRef.current = false;
      openedAgainstEditedAtRef.current = comment?.edited_at ?? null;
      const focusTimer = setTimeout(() => textareaRef.current?.focus(), 0);
      return () => clearTimeout(focusTimer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    if (!isSaving) {
      submittingRef.current = false;
    }
  }, [isSaving]);

  const authorName = actorDisplayName(comment);
  const validation = validateCommentText(draft);
  const isUnchanged = draft === originalText;
  // Save is disabled while empty/whitespace, over-length, or unchanged from
  // the original — there is nothing worth persisting in any of those cases
  // (AC-F4.4).
  const isInvalid = !validation.isValid || isUnchanged;
  const remaining = COMMENT_MAX_LENGTH - draft.length;
  const showValidation = touched && !validation.isValid;

  const editorId = `checklist-comment-editor-${comment?.id}`;
  const helpId = `checklist-comment-editor-help-${comment?.id}`;
  const validationId = `checklist-comment-editor-validation-${comment?.id}`;

  const handleChange = (event) => {
    setDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH));
  };

  const handleCancel = () => {
    if (isSaving || submittingRef.current) return;
    setDraft(originalText);
    setTouched(false);
    onCancelEdit();
  };

  const handleSave = () => {
    if (isSaving || submittingRef.current) return;
    if (isInvalid) {
      setTouched(true);
      return;
    }
    submittingRef.current = true;
    // Retrying after a conflict ("Keep mine") must use the FRESH edited_at
    // the container just learned via its refetch, not the stale value this
    // editor originally opened against — the latter would just conflict
    // again (AC-F10.1, AC-F10.2).
    const expectedEditedAt = staleConflict
      ? staleConflict.currentEditedAt
      : openedAgainstEditedAtRef.current;
    onSaveEdit(draft.trim(), expectedEditedAt);
  };

  // Text, not styling (AC-F3.5) — the "(edited)" marker carries the edited
  // timestamp on hover via `title`, never as a colour or icon alone.
  const editedMarker = comment?.edited_at ? (
    <span
      className="checklist-comment-item-edited"
      title={formatTimestamp(comment.edited_at)}
    >
      {" "}(edited)
    </span>
  ) : null;

  // canModerate is intentionally excluded from showEdit — see the prop doc
  // above and NG7/AC-F8.5.
  const showEdit = Boolean(isOwn);
  const showDelete = Boolean(isOwn) || Boolean(canModerate);

  return (
    <li className="checklist-comment-item">
      <div className="checklist-comment-item-body">
      <div className="checklist-comment-item-header">
        <span className="checklist-comment-item-author">
          {authorName}
          {editedMarker}
        </span>
        <time
          className="checklist-comment-item-timestamp"
          dateTime={comment?.created_at}
        >
          <ClockIcon />
          {formatTimestamp(comment?.created_at)}
        </time>
      </div>

      {isEditing ? (
        <div className="checklist-comment-item-editor">
          <label htmlFor={editorId} className="visually-hidden">
            Edit comment
          </label>
          <textarea
            id={editorId}
            ref={textareaRef}
            className="form-control checklist-comment-item-editor-textarea"
            rows={3}
            maxLength={COMMENT_MAX_LENGTH}
            value={draft}
            aria-describedby={showValidation ? `${helpId} ${validationId}` : helpId}
            aria-invalid={showValidation}
            onChange={handleChange}
            onBlur={() => setTouched(true)}
            disabled={isSaving}
          />
          <div
            className="checklist-comment-item-editor-meta"
            id={helpId}
            aria-live="polite"
          >
            {remaining} characters remaining
          </div>
          {showValidation ? (
            <div
              className="checklist-comment-item-editor-validation"
              id={validationId}
              role="alert"
            >
              Enter a comment before saving.
            </div>
          ) : null}
          {editError ? (
            <div className="checklist-comment-item-editor-error" role="alert">
              {editError}
            </div>
          ) : null}
          {staleConflict ? (
            // CLCMT-F011/F10 (AC-F10.3): the current server text shown
            // BESIDE the user's still-preserved draft, with an explicit
            // choice — never a silent overwrite in either direction.
            <div
              className="checklist-comment-item-stale-conflict"
              role="status"
            >
              <p className="checklist-comment-item-stale-conflict-label">
                Someone else changed this comment while you were editing.
                Their version:
              </p>
              <p className="checklist-comment-item-stale-conflict-text">
                {staleConflict.currentText}
              </p>
            </div>
          ) : null}
          <div className="checklist-comment-item-editor-actions">
            <button
              type="button"
              className="btn btn-secondary checklist-comment-item-cancel"
              style={{ minHeight: 44, minWidth: 44 }}
              disabled={isSaving}
              onClick={handleCancel}
            >
              {staleConflict ? "Discard" : "Cancel"}
            </button>
            <button
              type="button"
              className="btn primary-btn checklist-comment-item-save"
              style={{ minHeight: 44, minWidth: 44 }}
              disabled={isInvalid || Boolean(isSaving)}
              onClick={handleSave}
            >
              {isSaving ? "Saving…" : staleConflict ? "Keep mine" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <p className="checklist-comment-item-text">{comment?.comment}</p>
      )}

      {!isEditing && (showEdit || showDelete) ? (
        <div className="checklist-comment-item-actions">
          {showEdit ? (
            <button
              type="button"
              className="btn btn-link checklist-comment-item-edit"
              style={{ minWidth: 44 }}
              aria-label={`Edit comment by ${authorName}`}
              onClick={onStartEdit}
            >
              Edit
            </button>
          ) : null}
          {showDelete ? (
            <button
              type="button"
              className="btn btn-link checklist-comment-item-delete"
              style={{ minWidth: 44 }}
              aria-label={`Delete comment by ${authorName}`}
              onClick={onRequestDelete}
            >
              Delete
            </button>
          ) : null}
        </div>
      ) : null}
      </div>
    </li>
  );
};

export default ChecklistCommentItem;
